import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    setCollabMcqModalVisibility,
    setCollabMcqModalIdProject,
    getProjectQuestions,
    setCollabMcqQuestions,
    getProjects,
    recordMcqResponsePerProject,
    setCollabMcqResp
} from "./vcompSlice";
import { showErrorToast, showSuccessToast } from "../../app/utils/helpers";
import Langs from "../../app/lang/langs";

import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Checkbox from "@mui/material/Checkbox";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import CircularProgress from "@mui/material/CircularProgress";
import Fade from "@mui/material/Fade";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import CloseIcon from "@mui/icons-material/Close";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";

/* ── Slide-up transition for fullscreen dialog ── */
const SlideUp = React.forwardRef(function SlideUp(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

/* ── Colour constants (derived from existing palette) ── */
const ORANGE = "#FF9015";
const ORANGE_LIGHT = "rgba(255,144,21,0.08)";
const ORANGE_GLOW = "rgba(255,144,21,0.25)";
const ORANGE_GRADIENT = "linear-gradient(135deg, #FF8701, #FFC100)";
const SURFACE = "rgba(255,255,255,0.72)";
const BG_GRADIENT = "linear-gradient(160deg, #f7f8fa 0%, #eef0f4 50%, #e8eaef 100%)";
const TEXT_PRIMARY = "#111318";
const TEXT_SECONDARY = "#4b5263";
const TEXT_MUTED = "#8b92a0";
const BORDER_SUBTLE = "rgba(0,0,0,0.06)";
const RED = "#dc2626";
const GREEN = "#16a34a";
const EASE = "cubic-bezier(0.4,0,0.2,1)";

export default function CollabMcqView() {

    const dispatch = useDispatch();

    const visible = useSelector((state) => state.vcomp.collabMcqModalVisibility);
    const idProject = useSelector((state) => state.vcomp.collabMcqModalIdProject);
    const questions = useSelector((state) => state.vcomp.collabMcqQuestions);
    const projectsList = useSelector((state) => state.vcomp.projectsList);
    const respProjectMcq = useSelector((state) => state.vcomp.collabMcqResp);

    const [projectName, setProjectName] = useState('');
    const [selections, setSelections] = useState({});
    const [timeRemaining, setTimeRemaining] = useState(0);
    const [timerActive, setTimerActive] = useState(false);

    useEffect(() => {
        if (visible && idProject) {
            const project = projectsList.find(p => p.id == idProject);
            if (project) {
                setProjectName(project.nom);
            }
            dispatch(getProjectQuestions({ id_project: idProject }));
        } else {
            setTimerActive(false);
        }
    }, [visible]);

    useEffect(() => {
        let timer;
        if (questions.length > 0) {
            setTimeRemaining(120 * questions.length);
            timer = setInterval(() => {
                setTimeRemaining(time => {
                    if (time <= 1) {
                        clearInterval(timer);
                        return 0;
                    }
                    return time - 1;
                });
            }, 1000);
        } else {
            if (timer) {
                clearInterval(timer);
            }
        }
        return () => {
            if (timer) {
                clearInterval(timer);
            }
        };
    }, [questions]);

    useEffect(() => {
        if (questions.length > 0 && timeRemaining < 1) {
            handleSubmitMcq();
        }
    }, [timeRemaining]);

    useEffect(() => {
        if (respProjectMcq != null) {
            if (respProjectMcq.trim().toLowerCase().includes("successfully")) {
                handleClose();
                showSuccessToast(respProjectMcq);
            } else {
                handleClose();
            }
            dispatch(setCollabMcqResp(null));
        }
    }, [respProjectMcq]);

    const handleClose = () => {
        dispatch(getProjects());
        dispatch(setCollabMcqModalVisibility(false));
        dispatch(setCollabMcqModalIdProject(null));
        dispatch(setCollabMcqQuestions([]));
        setSelections({});
        setProjectName('');
        setTimeRemaining(0);
    };

    const allQuestionsAnswered = questions.every(question => {
        const selection = selections[question.id];
        return Array.isArray(selection) ? selection.length > 0 : selection !== undefined && selection !== null;
    });

    const handleSubmitMcq = () => {
        const response = Object.keys(selections).map(key => ({
            id_question: parseInt(key),
            answers: Array.isArray(selections[key]) ? selections[key] : [selections[key]]
        }));
        dispatch(recordMcqResponsePerProject({
            id_project: idProject,
            response: response
        }));
    };

    const handleSelectionChange = (questionId, optionId, isMulti) => {
        if (isMulti) {
            setSelections(prev => {
                const currentSelections = prev[questionId] || [];
                if (currentSelections.includes(optionId)) {
                    if (currentSelections.length === 1) {
                        return prev;
                    }
                    return {
                        ...prev,
                        [questionId]: currentSelections.filter(id => id !== optionId)
                    };
                } else {
                    return {
                        ...prev,
                        [questionId]: [...currentSelections, optionId]
                    };
                }
            });
        } else {
            setSelections(prev => ({
                ...prev,
                [questionId]: prev[questionId] === optionId ? undefined : optionId
            }));
        }
    };

    /* ── Timer helpers ── */
    const totalTime = useMemo(() => 120 * questions.length, [questions.length]);
    const timerPercent = totalTime > 0 ? (timeRemaining / totalTime) * 100 : 0;
    const isTimeLow = timeRemaining > 0 && timeRemaining <= 59;
    const timerColor = isTimeLow ? RED : ORANGE;

    function formatTime(seconds) {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }

    /* ── Count answered questions ── */
    const answeredCount = questions.filter(q => {
        const s = selections[q.id];
        return Array.isArray(s) ? s.length > 0 : s !== undefined && s !== null;
    }).length;

    /* ── Refs for scroll-to-question ── */
    const scrollContainerRef = useRef(null);
    const questionRefs = useRef({});

    const scrollToQuestion = useCallback((questionId) => {
        const el = questionRefs.current[questionId];
        const container = scrollContainerRef.current;
        if (el && container) {
            const containerTop = container.getBoundingClientRect().top;
            const elTop = el.getBoundingClientRect().top;
            const offset = elTop - containerTop + container.scrollTop - 16;
            container.scrollTo({ top: offset, behavior: "smooth" });
        }
    }, []);

    /* ── Keyframes (injected once) ── */
    const keyframes = `
        @keyframes mcqFadeInUp {
            from { opacity: 0; transform: translateY(16px); }
            to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes mcqPulseGlow {
            0%, 100% { box-shadow: 0 0 0 0 rgba(255,144,21,0.0); }
            50%      { box-shadow: 0 0 20px 4px rgba(255,144,21,0.18); }
        }
        @keyframes mcqTimerPulse {
            0%, 100% { transform: scale(1); }
            50%      { transform: scale(1.04); }
        }
        @keyframes mcqShimmer {
            0%   { background-position: -200% 0; }
            100% { background-position: 200% 0; }
        }
    `;

    return (
        <>
            <style>{keyframes}</style>
            <Dialog
                fullScreen
                open={visible}
                onClose={() => {}}
                slots={{ transition: SlideUp }}
                slotProps={{
                    transition: { timeout: 400 },
                    paper: {
                        sx: {
                            background: BG_GRADIENT,
                            overflow: "hidden",
                        }
                    }
                }}
                sx={{ zIndex: 1400 }}
            >
                {/* ── Top AppBar ── */}
                <AppBar
                    position="sticky"
                    elevation={0}
                    sx={{
                        background: "rgba(255,255,255,0.82)",
                        backdropFilter: "blur(16px)",
                        WebkitBackdropFilter: "blur(16px)",
                        borderBottom: `1px solid ${BORDER_SUBTLE}`,
                        color: TEXT_PRIMARY,
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            bottom: 0,
                            left: 0,
                            right: 0,
                            height: "2px",
                            background: `linear-gradient(90deg, transparent 10%, ${ORANGE_GLOW} 50%, transparent 90%)`,
                        }
                    }}
                >
                    <Toolbar sx={{ justifyContent: "space-between", px: { xs: 2, md: 4 }, minHeight: { xs: 64, md: 72 } }}>
                        {/* Left: project info */}
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Box sx={{
                                width: 36, height: 36, borderRadius: "10px",
                                background: ORANGE_LIGHT,
                                display: "flex", alignItems: "center", justifyContent: "center",
                            }}>
                                <QuizOutlinedIcon sx={{ color: ORANGE, fontSize: 20 }} />
                            </Box>
                            <Box>
                                <Typography sx={{
                                    fontSize: "1.05rem", fontWeight: 650,
                                    letterSpacing: "-0.02em", color: TEXT_PRIMARY,
                                    lineHeight: 1.2,
                                    fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
                                }}>
                                    {projectName || "Quiz"}
                                </Typography>
                                {questions.length > 0 && (
                                    <Typography sx={{
                                        fontSize: "0.75rem", color: TEXT_MUTED, fontWeight: 500,
                                        fontFamily: "'Inter',sans-serif",
                                    }}>
                                        {answeredCount}/{questions.length} répondu{answeredCount !== 1 ? "es" : "e"}
                                    </Typography>
                                )}
                            </Box>
                        </Stack>

                        {/* Center: Timer */}
                        {questions.length > 0 && (
                            <Fade in timeout={600}>
                                <Stack direction="row" alignItems="center" spacing={1.5}
                                    sx={{
                                        px: 2.5, py: 0.8,
                                        borderRadius: "14px",
                                        background: isTimeLow ? "rgba(220,38,38,0.06)" : "rgba(255,144,21,0.06)",
                                        border: `1px solid ${isTimeLow ? "rgba(220,38,38,0.15)" : "rgba(255,144,21,0.12)"}`,
                                        transition: `all 400ms ${EASE}`,
                                        animation: isTimeLow ? "mcqTimerPulse 1.2s ease-in-out infinite" : "none",
                                    }}
                                >
                                    <Box sx={{ position: "relative", display: "inline-flex" }}>
                                        <CircularProgress
                                            variant="determinate"
                                            value={timerPercent}
                                            size={32}
                                            thickness={3.5}
                                            sx={{
                                                color: timerColor,
                                                transition: `color 400ms ${EASE}`,
                                                "& .MuiCircularProgress-circle": {
                                                    transition: `stroke-dashoffset 1s linear`,
                                                    strokeLinecap: "round",
                                                }
                                            }}
                                        />
                                        <Box sx={{
                                            position: "absolute", inset: 0,
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                        }}>
                                            <TimerOutlinedIcon sx={{ fontSize: 14, color: timerColor, transition: `color 400ms ${EASE}` }} />
                                        </Box>
                                    </Box>
                                    <Typography sx={{
                                        fontFamily: "'JetBrains Mono','SF Mono',monospace",
                                        fontSize: "1.1rem",
                                        fontWeight: 700,
                                        letterSpacing: "0.04em",
                                        color: timerColor,
                                        transition: `color 400ms ${EASE}`,
                                        minWidth: "56px",
                                    }}>
                                        {formatTime(timeRemaining)}
                                    </Typography>
                                </Stack>
                            </Fade>
                        )}

                        {/* Right: placeholder for symmetry */}
                        <Box sx={{ width: 36 }} />
                    </Toolbar>
                </AppBar>

                {/* ── Body ── */}
                {questions.length > 0 ? (
                    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                        {/* ── Sticky progress chips bar ── */}
                        <Box sx={{
                            position: "sticky",
                            top: 0,
                            zIndex: 5,
                            background: "rgba(255,255,255,0.85)",
                            backdropFilter: "blur(12px)",
                            WebkitBackdropFilter: "blur(12px)",
                            borderBottom: `1px solid ${BORDER_SUBTLE}`,
                            py: 1.5,
                            px: { xs: 1.5, sm: 3, md: 0 },
                        }}>
                            <Stack direction="row" spacing={0.5} sx={{
                                maxWidth: 720, mx: "auto",
                                px: 1, flexWrap: "wrap", gap: 0.5,
                                justifyContent: "center",
                            }}>
                                {questions.map((q, i) => {
                                    const s = selections[q.id];
                                    const answered = Array.isArray(s) ? s.length > 0 : s !== undefined && s !== null;
                                    return (
                                        <Box
                                            key={q.id}
                                            onClick={() => scrollToQuestion(q.id)}
                                            sx={{
                                                width: 28, height: 28,
                                                borderRadius: "8px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                fontSize: "0.7rem",
                                                fontWeight: 700,
                                                fontFamily: "'Inter',sans-serif",
                                                cursor: "pointer",
                                                userSelect: "none",
                                                background: answered
                                                    ? ORANGE_GRADIENT
                                                    : "rgba(0,0,0,0.05)",
                                                color: answered ? "#fff" : TEXT_MUTED,
                                                border: `1px solid ${answered ? "transparent" : "rgba(0,0,0,0.08)"}`,
                                                transition: `all 300ms ${EASE}`,
                                                "&:hover": {
                                                    transform: "scale(1.15)",
                                                    boxShadow: answered
                                                        ? `0 2px 8px rgba(255,144,21,0.3)`
                                                        : `0 2px 8px rgba(0,0,0,0.1)`,
                                                },
                                                "&:active": { transform: "scale(0.95)" },
                                            }}
                                        >
                                            {i + 1}
                                        </Box>
                                    );
                                })}
                            </Stack>
                        </Box>

                        {/* ── Scrollable questions area ── */}
                        <Box
                            ref={scrollContainerRef}
                            sx={{
                                flex: 1,
                                overflowY: "auto",
                                overflowX: "hidden",
                                pb: "120px",
                                pt: 3,
                                px: { xs: 1.5, sm: 3, md: 0 },
                                /* custom scrollbar */
                                "&::-webkit-scrollbar": { width: "6px" },
                                "&::-webkit-scrollbar-track": { background: "transparent" },
                                "&::-webkit-scrollbar-thumb": {
                                    background: "rgba(255,144,21,0.18)",
                                    borderRadius: "3px",
                                    "&:hover": { background: "rgba(255,144,21,0.3)" }
                                },
                            }}
                        >
                            <Box sx={{ maxWidth: 720, mx: "auto" }}>
                            {/* Question cards */}
                            {questions.map((question, idx) => {
                                const isAnswered = (() => {
                                    const s = selections[question.id];
                                    return Array.isArray(s) ? s.length > 0 : s !== undefined && s !== null;
                                })();

                                return (
                                    <Paper
                                        key={question.id}
                                        ref={(el) => { questionRefs.current[question.id] = el; }}
                                        elevation={0}
                                        sx={{
                                            mb: 2.5,
                                            borderRadius: "18px",
                                            background: SURFACE,
                                            backdropFilter: "blur(12px)",
                                            WebkitBackdropFilter: "blur(12px)",
                                            border: `1px solid ${isAnswered ? "rgba(255,144,21,0.18)" : BORDER_SUBTLE}`,
                                            overflow: "hidden",
                                            position: "relative",
                                            transition: `all 350ms ${EASE}`,
                                            animation: `mcqFadeInUp 500ms ${EASE} ${idx * 80}ms both`,
                                            "&:hover": {
                                                border: `1px solid rgba(255,144,21,0.22)`,
                                                boxShadow: `0 4px 24px rgba(255,144,21,0.08), 0 1px 6px rgba(0,0,0,0.04)`,
                                                transform: "translateY(-1px)",
                                            },
                                            /* top accent bar */
                                            "&::before": {
                                                content: '""',
                                                position: "absolute",
                                                top: 0, left: 0, right: 0,
                                                height: "2px",
                                                background: isAnswered
                                                    ? ORANGE_GRADIENT
                                                    : `linear-gradient(90deg, transparent 10%, ${ORANGE_GLOW} 50%, transparent 90%)`,
                                                transition: `all 400ms ${EASE}`,
                                            }
                                        }}
                                    >
                                        {/* Question header */}
                                        <Box sx={{
                                            px: { xs: 2.5, md: 3.5 },
                                            pt: 2.5,
                                            pb: 1.5,
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: 1.5,
                                        }}>
                                            <Chip
                                                label={idx + 1}
                                                size="small"
                                                sx={{
                                                    minWidth: 30, height: 28,
                                                    borderRadius: "9px",
                                                    fontWeight: 700,
                                                    fontSize: "0.78rem",
                                                    mt: 0.3,
                                                    background: isAnswered
                                                        ? "rgba(255,144,21,0.12)"
                                                        : "rgba(0,0,0,0.04)",
                                                    color: isAnswered ? ORANGE : TEXT_MUTED,
                                                    border: `1px solid ${isAnswered ? "rgba(255,144,21,0.2)" : "rgba(0,0,0,0.06)"}`,
                                                    transition: `all 350ms ${EASE}`,
                                                    "& .MuiChip-label": { px: 1 },
                                                }}
                                            />
                                            <Box sx={{ flex: 1 }}>
                                                <Typography sx={{
                                                    fontSize: { xs: "1rem", md: "1.1rem" },
                                                    fontWeight: 600,
                                                    letterSpacing: "-0.02em",
                                                    color: TEXT_PRIMARY,
                                                    lineHeight: 1.45,
                                                    fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
                                                }}>
                                                    {question.question}
                                                </Typography>
                                                <Typography sx={{
                                                    fontSize: "0.72rem",
                                                    color: TEXT_MUTED,
                                                    fontWeight: 500,
                                                    mt: 0.3,
                                                    fontFamily: "'Inter',sans-serif",
                                                }}>
                                                    {question.type === "single" ? "Choix unique" : "Choix multiple"}
                                                </Typography>
                                            </Box>
                                            {isAnswered && (
                                                <Fade in timeout={300}>
                                                    <CheckCircleOutlineIcon sx={{
                                                        color: GREEN,
                                                        fontSize: 22,
                                                        mt: 0.4,
                                                        filter: "drop-shadow(0 1px 3px rgba(22,163,74,0.2))",
                                                    }} />
                                                </Fade>
                                            )}
                                        </Box>

                                        {/* Divider */}
                                        <Box sx={{
                                            height: "1px", mx: { xs: 2.5, md: 3.5 },
                                            background: `linear-gradient(90deg, transparent, ${BORDER_SUBTLE}, transparent)`,
                                        }} />

                                        {/* Options */}
                                        <Box sx={{ px: { xs: 2, md: 3 }, py: 1.5 }}>
                                            {question.type === "single" ? (
                                                <RadioGroup
                                                    value={selections[question.id] !== undefined ? selections[question.id] : ""}
                                                    onChange={(e) => {
                                                        const val = parseInt(e.target.value);
                                                        handleSelectionChange(question.id, val, false);
                                                    }}
                                                >
                                                    {question.options.map(option => {
                                                        const isSelected = selections[question.id] === option.id;
                                                        return (
                                                            <FormControlLabel
                                                                key={option.id}
                                                                value={option.id}
                                                                control={
                                                                    <Radio
                                                                        size="small"
                                                                        sx={{
                                                                            color: "rgba(0,0,0,0.2)",
                                                                            "&.Mui-checked": { color: ORANGE },
                                                                            transition: `all 250ms ${EASE}`,
                                                                        }}
                                                                    />
                                                                }
                                                                label={
                                                                    <Typography sx={{
                                                                        fontSize: "0.92rem",
                                                                        fontWeight: isSelected ? 550 : 420,
                                                                        color: isSelected ? TEXT_PRIMARY : TEXT_SECONDARY,
                                                                        transition: `all 250ms ${EASE}`,
                                                                        fontFamily: "'Inter',sans-serif",
                                                                    }}>
                                                                        {option.value}
                                                                    </Typography>
                                                                }
                                                                sx={{
                                                                    mx: 0,
                                                                    mb: 0.3,
                                                                    px: 1.5,
                                                                    py: 0.8,
                                                                    borderRadius: "12px",
                                                                    border: `1px solid ${isSelected ? "rgba(255,144,21,0.22)" : "transparent"}`,
                                                                    background: isSelected ? "rgba(255,144,21,0.04)" : "transparent",
                                                                    transition: `all 250ms ${EASE}`,
                                                                    "&:hover": {
                                                                        background: isSelected
                                                                            ? "rgba(255,144,21,0.07)"
                                                                            : "rgba(0,0,0,0.025)",
                                                                    },
                                                                }}
                                                            />
                                                        );
                                                    })}
                                                </RadioGroup>
                                            ) : (
                                                <FormGroup>
                                                    {question.options.map(option => {
                                                        const isSelected = selections[question.id]
                                                            ? selections[question.id].includes(option.id)
                                                            : false;
                                                        return (
                                                            <FormControlLabel
                                                                key={option.id}
                                                                control={
                                                                    <Checkbox
                                                                        size="small"
                                                                        checked={isSelected}
                                                                        onChange={() => handleSelectionChange(question.id, option.id, true)}
                                                                        sx={{
                                                                            color: "rgba(0,0,0,0.2)",
                                                                            "&.Mui-checked": { color: ORANGE },
                                                                            transition: `all 250ms ${EASE}`,
                                                                            borderRadius: "6px",
                                                                        }}
                                                                    />
                                                                }
                                                                label={
                                                                    <Typography sx={{
                                                                        fontSize: "0.92rem",
                                                                        fontWeight: isSelected ? 550 : 420,
                                                                        color: isSelected ? TEXT_PRIMARY : TEXT_SECONDARY,
                                                                        transition: `all 250ms ${EASE}`,
                                                                        fontFamily: "'Inter',sans-serif",
                                                                    }}>
                                                                        {option.value}
                                                                    </Typography>
                                                                }
                                                                sx={{
                                                                    mx: 0,
                                                                    mb: 0.3,
                                                                    px: 1.5,
                                                                    py: 0.8,
                                                                    borderRadius: "12px",
                                                                    border: `1px solid ${isSelected ? "rgba(255,144,21,0.22)" : "transparent"}`,
                                                                    background: isSelected ? "rgba(255,144,21,0.04)" : "transparent",
                                                                    transition: `all 250ms ${EASE}`,
                                                                    "&:hover": {
                                                                        background: isSelected
                                                                            ? "rgba(255,144,21,0.07)"
                                                                            : "rgba(0,0,0,0.025)",
                                                                    },
                                                                }}
                                                            />
                                                        );
                                                    })}
                                                </FormGroup>
                                            )}
                                        </Box>
                                    </Paper>
                                );
                            })}
                        </Box>
                    </Box>
                    </Box>
                ) : (
                    /* ── Empty state ── */
                    <Box sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                        animation: `mcqFadeInUp 500ms ${EASE} both`,
                    }}>
                        <Box sx={{
                            width: 72, height: 72, borderRadius: "20px",
                            background: "rgba(255,144,21,0.08)",
                            border: "1px solid rgba(255,144,21,0.12)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            mb: 1,
                        }}>
                            <QuizOutlinedIcon sx={{ fontSize: 32, color: ORANGE }} />
                        </Box>
                        <Typography sx={{
                            fontSize: "1.2rem", fontWeight: 600,
                            color: TEXT_PRIMARY, letterSpacing: "-0.02em",
                            fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
                        }}>
                            Aucune question
                        </Typography>
                        <Typography sx={{
                            fontSize: "0.88rem", color: TEXT_MUTED, fontWeight: 400,
                            fontFamily: "'Inter',sans-serif",
                        }}>
                            Il n'y a pas de questions pour ce projet.
                        </Typography>
                        <Button
                            variant="outlined"
                            onClick={() => handleClose()}
                            startIcon={<CloseIcon sx={{ fontSize: 18 }} />}
                            sx={{
                                mt: 2,
                                borderRadius: "12px",
                                px: 3.5, py: 1,
                                textTransform: "none",
                                fontWeight: 600,
                                fontSize: "0.88rem",
                                color: RED,
                                borderColor: "rgba(220,38,38,0.25)",
                                fontFamily: "'Inter',sans-serif",
                                transition: `all 250ms ${EASE}`,
                                "&:hover": {
                                    borderColor: RED,
                                    background: "rgba(220,38,38,0.05)",
                                    transform: "translateY(-1px)",
                                },
                                "&:active": { transform: "scale(0.98)" },
                            }}
                        >
                            <Langs str="CLOSE" />
                        </Button>
                    </Box>
                )}

                {/* ── Sticky bottom bar ── */}
                {questions.length > 0 && (
                    <Box sx={{
                        position: "fixed",
                        bottom: 0, left: 0, right: 0,
                        zIndex: 10,
                        background: "rgba(255,255,255,0.85)",
                        backdropFilter: "blur(16px)",
                        WebkitBackdropFilter: "blur(16px)",
                        borderTop: `1px solid ${BORDER_SUBTLE}`,
                        px: { xs: 2, md: 4 },
                        py: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 2,
                        animation: `mcqFadeInUp 400ms ${EASE} 200ms both`,
                    }}>
                        {/* Progress indicator */}
                        <Chip
                            label={`${answeredCount} / ${questions.length}`}
                            size="small"
                            sx={{
                                fontWeight: 700,
                                fontSize: "0.8rem",
                                fontFamily: "'Inter',sans-serif",
                                background: allQuestionsAnswered
                                    ? "rgba(22,163,74,0.08)"
                                    : "rgba(0,0,0,0.04)",
                                color: allQuestionsAnswered ? GREEN : TEXT_MUTED,
                                border: `1px solid ${allQuestionsAnswered ? "rgba(22,163,74,0.18)" : "rgba(0,0,0,0.06)"}`,
                                borderRadius: "10px",
                                transition: `all 350ms ${EASE}`,
                                "& .MuiChip-label": { px: 1.5 },
                            }}
                        />

                        {/* Submit button */}
                        <Button
                            variant="contained"
                            disabled={!allQuestionsAnswered}
                            onClick={() => handleSubmitMcq()}
                            endIcon={<SendRoundedIcon sx={{ fontSize: "18px !important" }} />}
                            sx={{
                                borderRadius: "14px",
                                px: 4, py: 1.2,
                                textTransform: "none",
                                fontWeight: 650,
                                fontSize: "0.92rem",
                                letterSpacing: "-0.01em",
                                fontFamily: "'Inter','Plus Jakarta Sans',sans-serif",
                                background: ORANGE_GRADIENT,
                                color: "#fff",
                                boxShadow: `0 4px 16px rgba(255,144,21,0.25), 0 1px 4px rgba(255,144,21,0.15)`,
                                transition: `all 300ms ${EASE}`,
                                "&:hover": {
                                    background: ORANGE_GRADIENT,
                                    boxShadow: `0 6px 24px rgba(255,144,21,0.35), 0 2px 8px rgba(255,144,21,0.2)`,
                                    transform: "translateY(-1px)",
                                },
                                "&:active": {
                                    transform: "scale(0.98)",
                                },
                                "&.Mui-disabled": {
                                    background: "rgba(0,0,0,0.06)",
                                    color: "rgba(0,0,0,0.26)",
                                    boxShadow: "none",
                                },
                            }}
                        >
                            Soumettre
                        </Button>
                    </Box>
                )}
            </Dialog>
        </>
    );
}
